import React, { useState, useEffect } from 'react';

const DeviceSelectorModal = ({ isOpen, onClose, onDeviceSelected }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchDevices();
    }
  }, [isOpen]);

  const fetchDevices = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/v1/devices', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch devices');
      }
      
      const data = await response.json();
      setDevices(data.devices || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceSelect = (device) => {
    onDeviceSelected(device);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Select RFID Reader</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-700 rounded-full w-8 h-8 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading devices...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {!loading && !error && devices.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No devices found for your company.
            </div>
          )}

          {!loading && !error && devices.length > 0 && (
            <div className="space-y-2">
              {devices.map((device) => (
                <button
                  key={device.device_id}
                  onClick={() => handleDeviceSelect(device)}
                  className="w-full text-left bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-400 rounded-lg p-4 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">
                        {device.device_name || device.device_id}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        MAC: {device.device_id}
                      </div>
                      {device.firmware_version && (
                        <div className="text-xs text-gray-500 mt-1">
                          FW: {device.firmware_version}
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          device.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {device.status}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-4 border-t">
          <p className="text-xs text-gray-600 text-center">
            Select a device to start QR code scanning
          </p>
        </div>
      </div>
    </div>
  );
};

export default DeviceSelectorModal;

